import { EXTERNAL_ADDRESS, FACEBOOK_APP_ID } from "~/app/environment/server";
import { PROFILE_LISTING_USE_TYPE_ORDER } from "~/constants/listing.consts";
import { ListingUseType } from "~/types/listing.types";
import {
  CurrentLocaleData,
  GroupedListings,
  ProfileListing,
  ProfileListingView,
  PublicProfile,
} from "~/types/profile.types";
import { VendorShareData } from "~/types/vendor.type";

/**
 * Builds Facebook / X / email share payloads for a profile page, mirroring the vendor page's
 * `useVendorShareData`. Reuses `VendorShareData` since the `ShareModal` consumes that shape.
 */
const buildProfileShareData = ({
  currentLocaleData,
  profile,
}: {
  currentLocaleData: CurrentLocaleData;
  profile: PublicProfile;
}): VendorShareData => {
  const localeUrlPrefix = currentLocaleData?.data?.url_prefix;
  const userLocaleShare = localeUrlPrefix ? `${localeUrlPrefix}/` : "";
  const shareUrl = `${EXTERNAL_ADDRESS}/${userLocaleShare}profiles/${profile.SSOid}`;
  const fullName =
    `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim();

  return {
    fbShare: {
      appId: FACEBOOK_APP_ID,
      url: shareUrl,
      winWidth: 520,
      winHeight: 350,
    },
    xShare: { city: "", url: shareUrl, winWidth: 450, winHeight: 550 },
    emailShare: `mailto:?subject=Check out this Peerspace host&body=${encodeURIComponent(
      fullName,
    )} - ${shareUrl}`,
  };
};

/**
 * Builds Facebook / X / email share payloads for a single listing on the profile page,
 * pointing at the listing detail route. Mirrors the listing page's share-URL construction.
 */
const buildListingShareData = ({
  currentLocaleData,
  listing,
}: {
  currentLocaleData: CurrentLocaleData;
  listing: ProfileListing;
}): VendorShareData => {
  const localeUrlPrefix = currentLocaleData?.data?.url_prefix;
  const userLocaleShare = localeUrlPrefix ? `${localeUrlPrefix}/` : "";
  const shareUrl = `${EXTERNAL_ADDRESS}/${userLocaleShare}pages/listings/${listing._id}`;

  return {
    fbShare: {
      appId: FACEBOOK_APP_ID,
      url: shareUrl,
      winWidth: 520,
      winHeight: 350,
    },
    xShare: {
      city: listing.city?.replace(/\s+/g, "") || "",
      url: shareUrl,
      winWidth: 450,
      winHeight: 550,
    },
    emailShare: `mailto:?subject=Check out this Peerspace location&body=${encodeURIComponent(
      listing.title,
    )} - ${shareUrl}`,
  };
};

/**
 * Groups a user's listings into the three profile-page sections (Off-Site, Event, Production)
 * by their primary use-type, mirroring web-templates' `_.groupBy(filteredListings, 'use_type')`.
 */
const groupListingsByUseType = (
  listings: ProfileListingView[],
): GroupedListings => {
  const grouped: GroupedListings = {
    "Off-Site": [],
    Event: [],
    Production: [],
  };

  listings.forEach((listing) => {
    const useType = listing.uses?.[0]?.name as ListingUseType | undefined;
    if (useType && PROFILE_LISTING_USE_TYPE_ORDER.includes(useType)) {
      grouped[useType].push(listing);
    }
  });

  return grouped;
};

/**
 * Returns true when the profile belongs to a Power Host, mirroring the legacy
 * `host_state === 'POWER_HOST'` check.
 */
const getIsPowerHost = (profile: PublicProfile): boolean =>
  profile.host_state === "POWER_HOST";

const getFullName = (
  profile: Pick<PublicProfile, "first_name" | "last_name">,
): string => `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim();

/**
 * Builds a Cloudinary avatar URL from a profile's `avatar_id`, falling back to the
 * Peerspace placeholder when the user has no avatar.
 */
const getProfileAvatarUrl = (avatarId?: string, width = 180): string =>
  avatarId
    ? `https://img.peerspace.com/image/upload/c_crop,g_custom/g_auto,c_fill,q_auto,f_auto,fl_progressive:steep,w_${width}/${avatarId}`
    : "";

/**
 * Builds a Cloudinary listing hero-image URL from a listing image's `external_id`,
 * matching the watermarked transform used by the legacy profile listing cards.
 */
const getListingHeroImageUrl = (externalId?: string): string =>
  externalId
    ? `https://img.peerspace.com/image/upload/c_crop,g_custom/c_fill,f_auto,fl_progressive,h_298,w_600/c_scale,g_north_east,l_Peerspace_Watermark_Logo,o_0,w_100,x_20,y_20/dpr_2.0/${externalId}`
    : "";

/** Formats a join date as e.g. "Jun 2026" for the profile header. */
const formatJoinedDate = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
};

/** Formats a review date as a short, locale-aware date string. */
const formatReviewDate = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

/** Formats a listing price with its currency symbol, e.g. "$1,234". */
const formatListingPrice = (value?: number, currency?: string): string => {
  if (typeof value !== "number") return "";
  if (!currency) return `${value}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value}`;
  }
};

/**
 * A guest's name as the home page credits it: "Onur" / "Erdem" becomes "Onur E.". The first name stays as typed.
 * @param {{ first_name?: string | null, last_name?: string | null } | undefined} guestName - As the guest typed it.
 * @returns {string} "First L.", just "First" without a last name, or "" without either.
 */
const formatGuestDisplayName = ({
  first_name,
  last_name,
}: {
  first_name?: string | null;
  last_name?: string | null;
} = {}): string => {
  const firstName = first_name?.trim() ?? "";
  const lastInitial = last_name?.trim().charAt(0).toUpperCase();

  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
};

export {
  buildListingShareData,
  buildProfileShareData,
  getFullName,
  getIsPowerHost,
  getListingHeroImageUrl,
  getProfileAvatarUrl,
  groupListingsByUseType,
  formatGuestDisplayName,
  formatJoinedDate,
  formatListingPrice,
  formatReviewDate,
};
